/**
 * Primitives shared between the SDK-ported layout engine
 * (`container-dimensions-computer.ts`, in `common/`) and the creative-tag
 * `ContainerDimensionRetriever`. They live in `common/` because the dependency
 * direction is one-way: `creative-tag/` may import `common/`, never the reverse.
 *
 * Behavioral parity with the SDK is the bar; each primitive reproduces the exact
 * logic the two call sites used before being de-duplicated.
 */

import { Dimensions } from './types';

/**
 * The SDK's pop-up Interstitial fallback size. Used by the layout engine as an
 * `expectedContainerDimensions` workaround value and by the retriever as the
 * last-resort tracking size.
 */
export const POPUP_INTERSTITIAL_DIMENSIONS: Dimensions = {
  width: 320,
  height: 480
};

/**
 * Parses a CSS pixel string by stripping its last two characters (the `px`
 * unit) and reading the leading number. Faithful to the SDK's `parsePixels`:
 * when `checkPixelUnit` is `true` and the value carries no `px`, returns `0`.
 */
export function parseCssPixels(
  value: string,
  checkPixelUnit?: boolean
): number {
  if (checkPixelUnit === true && value.indexOf('px') === -1) {
    return 0;
  }
  return Number.parseFloat(value.substring(0, value.length - 2));
}

/**
 * Parses the OCS `creative_size` macro (`"WxH"`). Canonical semantics: exactly
 * two `x`-separated tokens, base-10 integers, both strictly positive. Any other
 * shape yields `undefined`. Identical to the previous call-site logic for every
 * real macro value.
 */
export function parseCreativeSizeMacro(
  raw: string | undefined
): Dimensions | undefined {
  if (raw === undefined) {
    return undefined;
  }
  const tokens = raw.split('x');
  if (tokens.length !== 2) {
    return undefined;
  }
  const width = Number.parseInt(tokens[0], 10);
  const height = Number.parseInt(tokens[1], 10);
  if (width > 0 && height > 0) {
    return { width, height };
  }
  return undefined;
}
